import * as Types from '../gql/_types.js';

import { AreaModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = AreaModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetAreasQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.areas.errors = [];
    state.areas.loading = true;
    try {
        let { limit, offset, orderBy } = state.areas.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetAreas({...originalPayload, ...finalPayload})
        state.areas.meta.total = meta.aggregate?.count || 0;
        state.areas.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
    }
    state.areas.loading = false;
}

export const resetState: Action = ({state}) => {
    state.areas.errors = []
    state.areas.loading = false
    state.areas.items = {}
    state.areas.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.areas.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.areas.resetMeta()
    actions.areas.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.areas.errors = [];
    state.areas.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.areas.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetAreas({...originalPayload, ...finalPayload})
        state.areas.meta.total = meta.aggregate?.count || 0;
        state.areas.meta.page = newPage;
        state.areas.meta.offset = newOffset;
        state.areas.items = { ...state.areas.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
    }
    state.areas.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetAreasQueryVariables> = async ({state, effects}, payload) => {
    state.areas.errors = [];
    state.areas.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetAreas(payload)
        state.areas.meta.total = meta.aggregate?.count || 0;
        state.areas.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
    }
    state.areas.loading = false;
}

export const getItem: AsyncAction<Types.GetAreaQueryVariables,Types.GetAreaQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetArea(payload);
        return item;
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetAreaQueryVariables> = async ({state, actions}, payload) => {
    state.areas.loading = true;
    try {
        let item = await actions.areas.getItem(payload);
        if (item) {
            state.areas.currentItemId = item.id;
            state.areas.items[item.id] = item;
        }
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
    }
    state.areas.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.areas.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.areas.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.AreaFragment>> = async ({state}, meta) => {
    state.areas.meta = {
        ...state.areas.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateAreaMutationVariables['object'],Types.CreateAreaMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateAreaMutationVariables['object']>(payload);
    state.areas.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateArea({object:finalPayload});
        return item;
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateAreaMutationVariables,Types.UpdateAreaMutation['item']> = async ({state, effects}, payload) => {
    state.areas.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateArea(payload);
        return item;
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteAreaMutationVariables,Types.DeleteAreaMutation['item']> = async ({state, effects}, payload) => {
    state.areas.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteArea(payload);
        return item;
    } catch (e: any) {
        state.areas.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetAreasMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetAreasMetaSubscription, Types.SyncGetAreasMetaSubscriptionVariables>(
        'syncGetAreasMeta', 
        subscriptions.SyncGetAreasMeta, 
        { where }, 
        actions.areas.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetAreasMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetAreasMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.areas.errors = [ ...state.areas.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.areas.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetAreasSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.areas.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.areas.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetAreasSubscription, Types.SyncGetAreasSubscriptionVariables>(
        'syncGetAreas', 
        subscriptions.SyncGetAreas, 
        {...originalPayload, ...payload}, 
        actions.areas.onItemsData,
        () => { state.areas.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetAreas');
    state.areas.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetAreasSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.areas.errors = [ ...state.areas.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.areas.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetAreasSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.areas.syncGetItems(finalPayload);
    actions.areas.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.areas.unsubItemsData();
    actions.areas.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetAreaSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.areas.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetAreaSubscription, Types.SyncGetAreaSubscriptionVariables>(
        'syncGetArea', 
        subscriptions.SyncGetArea, 
        payload, 
        actions.areas.onItemData,
        () => { state.areas.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetArea');
    state.areas.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetAreaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.areas.errors = [ ...state.areas.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.areas.currentItemId = payload.data.item.id;
        state.areas.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetAreaSubscriptionVariables> = async ({actions}, payload) => {
    actions.areas.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.areas.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};